import React, { useEffect, useRef } from "react";
import type { OrderLocation } from "./InfoPopup";
import type { OptimizedDriverRoute } from "./getOptimizedRoute";
import { routeOnce, clusterByDistance, buildMarkerDefsContiguous } from "./MapRouteUtils";
import { orderPinColor, orderPinIcon } from "./map-order-icon";
import type { RouteMetrics } from "./route-preview";
import { makeStopIcon, makeDepotIcon, routeColor } from "./route-map-style";
import { afterMapPaint } from "./map-paint";
import { attachRouteOrderPopup } from "./route-order-popup";

type Props = { orders: OrderLocation[]; routes: OptimizedDriverRoute[];
  onReady: (ready: boolean) => void; onError: (message: string) => void; onMetrics?: (metrics: RouteMetrics[]) => void;
  map?: google.maps.Map | null; onSetEndpoint?: (driverId: number, orderId: string, end: "start" | "end") => void;
  onReverseRoute?: (driverId: number) => void; onRemoveFromRoute?: (orderId: string) => void };

export default function RoutePreviewMap({ orders, routes, onReady, onError, onMetrics, map: sharedMap, onSetEndpoint, onReverseRoute, onRemoveFromRoute }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const actions = useRef({ onSetEndpoint, onReverseRoute, onRemoveFromRoute }); actions.current = { onSetEndpoint, onReverseRoute, onRemoveFromRoute };
  useEffect(() => {
    if (sharedMap !== undefined) {
      map.current = sharedMap;
      return () => { map.current = null; }; // The parent owns the main map, polygon, stores and other orders.
    }
    if (!window.google?.maps || !container.current) return;
    map.current = new google.maps.Map(container.current, { center: orders[0] ?? { lat: 12.3, lng: 76.6 }, zoom: 12 });
    return () => { if (map.current) google.maps.event.clearInstanceListeners(map.current); map.current = null; };
    // Standalone callers reuse the Maps script already loaded by Smart Map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedMap]);

  useEffect(() => {
    onReady(false); onError(""); onMetrics?.([]);
    const currentMap = map.current;
    if (!currentMap) {
      if (sharedMap !== null) onError("Google Map is unavailable. Close this preview and try again.");
      return;
    }
    let cancelled = false;
    const markers: google.maps.Marker[] = [];
    const lines: google.maps.Polyline[] = [];
    const popups: Array<() => void> = [];
    const bounds = new google.maps.LatLngBounds();
    const visits = new Map(routes.flatMap(r => r.stops.map(o => [String(o.orderId), { route: r, order: o }] as const)));
    const pin = (position: { lat: number; lng: number }, label: string, title: string, icon: google.maps.Symbol, route: OptimizedDriverRoute) => {
      bounds.extend(position);
      const marker = new google.maps.Marker({ map: currentMap, position, label: { text: label, color: "white", fontWeight: "bold" }, title, icon, zIndex: 1000 });
      markers.push(marker);
      const group = route.stops.filter(order => Math.abs(order.lat - position.lat) < 1e-6 && Math.abs(order.lng - position.lng) < 1e-6);
      if (group.length) popups.push(attachRouteOrderPopup(marker, currentMap, group, {
        onSetEndpoint: (orderId, end) => actions.current.onSetEndpoint?.(route.driverId, orderId, end),
        onReverseRoute: () => actions.current.onReverseRoute?.(route.driverId),
        onRemoveFromRoute: orderId => actions.current.onRemoveFromRoute?.(orderId), removeDisabled: orders.length <= 1,
      }));
    };
    const groups = new Map<string, OrderLocation[]>();
    orders.forEach(order => {
      if (sharedMap !== undefined || visits.has(String(order.orderId))) return;
      const key = `${order.lat},${order.lng}`;
      groups.set(key, [...(groups.get(key) ?? []), order]);
    });
    groups.forEach(group => {
      const position = { lat: group[0].lat, lng: group[0].lng };
      bounds.extend(position);
      markers.push(new google.maps.Marker({ map: currentMap, position, icon: orderPinIcon(orderPinColor(group)), zIndex: 100,
        title: group.map(o => `${o.orderId}: ${o.customerName}`).join("\n"),
      }));
    });
    routes.forEach(route => {
      const stops = [...route.stops].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
      buildMarkerDefsContiguous(clusterByDistance(stops)).forEach(stop => {
        bounds.extend(stop.pos);
        const marker = new google.maps.Marker({ map: currentMap, position: stop.pos, icon: makeStopIcon(routeColor(route.color)),
          label: { text: stop.label, color: "white", fontWeight: "bold" }, title: `${route.driverName} • ${stop.title}`, zIndex: 100 });
        markers.push(marker);
        const group = stops.filter(order => stop.orderIds?.includes(String(order.orderId)));
        popups.push(attachRouteOrderPopup(marker, currentMap, group, {
          onSetEndpoint: (orderId, end) => actions.current.onSetEndpoint?.(route.driverId, orderId, end),
          onReverseRoute: () => actions.current.onReverseRoute?.(route.driverId),
          onRemoveFromRoute: orderId => actions.current.onRemoveFromRoute?.(orderId), removeDisabled: orders.length <= 1,
        }));
      });
    });
    if (sharedMap === undefined && (routes.length || orders.length)) currentMap.fitBounds(bounds);
    const draw = async () => {
      const metrics: RouteMetrics[] = [];
      for (const route of routes) {
        if (cancelled) return;
        const stops = [...route.stops].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
        const points = [route.start!, ...stops, route.end!].filter((p, i, all) => p &&
          (i === 0 || !all[i - 1] || p.lat !== all[i - 1].lat || p.lng !== all[i - 1].lng));
        const path: google.maps.LatLng[] = [];
        let meters = 0, seconds = 0;
        // Google Directions permits at most 25 locations in one request.
        for (let start = 0; start < points.length - 1; start += 24) {
          const segment = points.slice(start, start + 25);
          const response = await routeOnce(new google.maps.DirectionsService(), {
            origin: segment[0], destination: segment[segment.length - 1],
            waypoints: segment.slice(1, -1).map(location => ({ location, stopover: true })),
            optimizeWaypoints: false, travelMode: google.maps.TravelMode.DRIVING,
          });
          if (cancelled) return;
          if (!response.routes[0]) throw new Error("No road route was found.");
          for (const leg of response.routes[0].legs ?? []) {
            if (!leg.distance || !leg.duration) throw new Error("Road distance or travel time is unavailable.");
            meters += leg.distance.value; seconds += leg.duration.value;
          }
          path.push(...response.routes[0].overview_path);
        }
        if (cancelled) return;
        const color = routeColor(route.color);
        lines.push(new google.maps.Polyline({ map: currentMap, path, strokeColor: color, strokeOpacity: 0.95, strokeWeight: 5 }));
        if (route.start) pin(route.start, "S", `${route.driverName} — Start`, makeDepotIcon("start", color), route);
        if (route.end) pin(route.end, "E", `${route.driverName} — End`, makeDepotIcon("end", color), route);
        metrics.push({ driverId: route.driverId, distanceKm: meters / 1000, drivingMinutes: seconds / 60, orderCount: route.stops.length });
      }
      await afterMapPaint();
      if (!cancelled && routes.length) {
        if (sharedMap === undefined) currentMap.fitBounds(bounds);
        onMetrics?.(metrics); onReady(true);
      }
    };
    void draw().catch(() => {
      if (!cancelled) { lines.forEach(line => line.setMap(null)); onError("Could not draw the road route. Recalculate or adjust the endpoints."); }
    });
    return () => {
      cancelled = true;
      popups.forEach(close => close());
      markers.forEach(marker => { google.maps.event.clearInstanceListeners(marker); marker.setMap(null); });
      lines.forEach(line => line.setMap(null));
    };
  }, [orders, routes, onReady, onError, onMetrics, sharedMap]);
  return sharedMap !== undefined ? null : <div ref={container} aria-label="Route preview map" style={{ width: "100%", height: "100%", minHeight: 420 }} />;
}
